import epsClient from 'enrollment-platform/api/eps.client'
import { type SurecoCsvDataType } from '../constants'

// ---------------------------------------------------------------------------
// Health Plan tab — stats, upload, and upload logs.
//
// Backed by the plan-management-v2 health-plans/* routes. Stats are still
// hardcoded server-side. Import runs as a queue job — the backend streams,
// validates, compares, and applies the uploaded CSV against the live tables.
// ---------------------------------------------------------------------------

const HEALTH_PLAN_BASE_PATH = '/api/v1/plan-management-v2/health-plans'

export interface HealthPlanStatsDto {
  totalPlans: number
  plansForSale: number
  statesCovered: number
  serviceAreas: number
}

export interface HealthPlanValidationSummaryDto {
  serviceAreaMismatchCount: number
  missingRatingAreaPriceCount: number
  missingBenefitsCount: number
  countyMismatchCount: number
}

export interface HealthPlanUploader {
  id: string
  name: string
}

export interface HealthPlanUploadLogS3Dto {
  originalFile: string | null
  writeSql: string | null
  diff: string | null
  rollback: string | null
  originalValues: string | null
  errors: string | null
}

export interface HealthPlanUploadLogAdditionalDataDto {
  success: boolean
  originalFilename: string
  dataType: SurecoCsvDataType
  states: string[] | null
  totalRows: number
  inserted: number
  updated: number
  rowErrors: number
  errorMessage?: string | null
  s3: HealthPlanUploadLogS3Dto
}

export interface HealthPlanUploadLogDto {
  id: number
  timestamp: string
  actorId: string
  actorName: string
  additionalData: HealthPlanUploadLogAdditionalDataDto
}

export interface HealthPlanImportResultDto {
  success: boolean
  totalRows: number
  inserted: number
  updated: number
  rowErrors: number
  errorMessage: string | null
}

export async function getHealthPlanStats(
  year: number
): Promise<HealthPlanStatsDto> {
  const { data } = await epsClient.get<HealthPlanStatsDto>(
    `${HEALTH_PLAN_BASE_PATH}/stats`,
    { params: { year } }
  )
  return data
}

export async function getHealthPlanValidationSummary(
  year: number
): Promise<HealthPlanValidationSummaryDto> {
  const { data } = await epsClient.get<HealthPlanValidationSummaryDto>(
    `${HEALTH_PLAN_BASE_PATH}/validation-summary`,
    { params: { year } }
  )
  return data
}

export async function getHealthPlanUploaders(): Promise<HealthPlanUploader[]> {
  const { data } = await epsClient.get<{ uploaders: HealthPlanUploader[] }>(
    `${HEALTH_PLAN_BASE_PATH}/uploaders`
  )
  return data.uploaders
}

export async function getHealthPlanUploadLogs(params: {
  page: number
  limit: number
  actorIds?: string[]
}): Promise<{ rows: HealthPlanUploadLogDto[]; total: number }> {
  const { data } = await epsClient.get<{
    rows: HealthPlanUploadLogDto[]
    total: number
  }>(`${HEALTH_PLAN_BASE_PATH}/upload-logs`, {
    params: {
      page: params.page,
      limit: params.limit,
      ...(params.actorIds?.length ? { actorIds: params.actorIds } : {}),
    },
  })
  return data
}

export async function getHealthPlanUploadLogDownloadUrl(
  s3Key: string
): Promise<string> {
  const { data } = await epsClient.get<{ url: string }>(
    `${HEALTH_PLAN_BASE_PATH}/upload-logs/download-url`,
    { params: { key: s3Key } }
  )
  return data.url
}

export async function presignHealthPlanUpload(
  originalFilename: string,
  fileSize: number
): Promise<{ folder: string; uploadUrl: string }> {
  const { data } = await epsClient.post<{ folder: string; uploadUrl: string }>(
    `${HEALTH_PLAN_BASE_PATH}/import/presigned-upload-url`,
    { originalFilename, fileSize }
  )
  return data
}

// The import runs as a queue job (large files can take a while to stream +
// validate) — this only enqueues it and returns a jobId. Poll
// getHealthPlanImportJob(jobId) for state/output, matching the Plan Benefits
// import flow.
export async function startHealthPlanImport(params: {
  folder: string
  originalFilename: string
  dataType: SurecoCsvDataType
}): Promise<{ jobId: string }> {
  const { data } = await epsClient.post<{ jobId: string }>(
    `${HEALTH_PLAN_BASE_PATH}/import`,
    params
  )
  return data
}

// Raw pg-boss job row from GET /jobs/:id — only the fields this feature reads.
export interface HealthPlanImportJobDto {
  id: string
  state:
    | 'created'
    | 'active'
    | 'completed'
    | 'failed'
    | 'retry'
    | 'cancelled'
    | 'expired'
  output: HealthPlanImportResultDto | null
}

export async function getHealthPlanImportJob(
  jobId: string
): Promise<HealthPlanImportJobDto> {
  const { data } = await epsClient.get<HealthPlanImportJobDto>(
    `/jobs/${jobId}`,
    {
      // Polled every few seconds while a job is in progress — must never be
      // served from a browser/proxy cache, or the UI can get stuck showing a
      // stale (e.g. "active") state forever even after the job completes.
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      params: { _: Date.now() },
    }
  )
  return data
}

export interface ActiveHealthPlanImportJobDto {
  jobId: string | null
  originalFilename: string | null
}

export async function getActiveHealthPlanImportJob(
  dataType: SurecoCsvDataType
): Promise<ActiveHealthPlanImportJobDto> {
  const { data } = await epsClient.get<ActiveHealthPlanImportJobDto>(
    `${HEALTH_PLAN_BASE_PATH}/import/active-job`,
    { params: { dataType } }
  )
  return data
}
